import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  AllInbox as AllInboxIcon,
  Warehouse as WarehouseIcon,
  LocalShipping as LocalShippingIcon,
  CloudSync as CloudSyncIcon,
} from '@mui/icons-material';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
} from 'recharts';
import axios from '../../utils/axios';
import { useNotify } from '../../hooks';
import PageHeader from '../../components/ui/PageHeader';
import ProductFilters, { buildFilterParams } from '../../components/ui/ProductFilters';
import { MP_MAP, MP_COLORS, MP_TEXT_COLORS, getWbImageUrl, toThumbnail } from '../../utils/productImage';

const chartColors = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f', '#0288d1', '#7b1fa2', '#5d4037', '#455a64', '#689f38'];

// Транзитные bucket'ы (не физсклады) — фиксированный порядок и подписи колонок
const TRANSIT_COLUMNS = [
  { key: 'to_customer', label: 'В пути к клиенту' },
  { key: 'at_pickup', label: 'Ожидает в ПВЗ' },
  { key: 'returns', label: 'Возвраты от клиента' },
  { key: 'supply', label: 'Поставки в пути на склад' },
];
const TRANSIT_BUCKETS = new Set(TRANSIT_COLUMNS.map((c) => c.key));
const TRANSIT_LABEL_BY_BUCKET = Object.fromEntries(TRANSIT_COLUMNS.map((c) => [c.key, c.label]));

const TOP_N = 9; // для donut: топ-N + «Прочее»

const fmtInt = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};
const fmtMoney = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};
const fmtPct = (v) => {
  const n = Number(v || 0);
  return `${n.toLocaleString('ru-RU', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
};

// Формат «гггг.мм.дд чч:мм» в локальном времени. Принимает ISO/DATETIME строку.
const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}.${p(d.getMonth() + 1)}.${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

// KPI-карточка: две крупные строки одинакового размера (стоимость + количество)
function KpiCard({ title, money, units, icon, color = 'primary' }) {
  const theme = useTheme();
  const p = theme.palette[color] || theme.palette.primary;
  const colors = { bg: p.light, text: p.dark, icon: p.main };
  return (
    <Card
      sx={{
        height: '100%',
        overflow: 'hidden',
        bgcolor: alpha(colors.bg, 0.3),
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          {icon && (
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: colors.bg,
                color: colors.text,
                '& .MuiSvgIcon-root': { fontSize: 24 },
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
        <Typography variant="titleMedium" color="text.secondary" sx={{ mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="headlineLarge" component="div" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.15 }}>
          {fmtMoney(money)} ₽
        </Typography>
        <Typography variant="headlineLarge" component="div" sx={{ fontWeight: 600, color: 'text.primary', lineHeight: 1.15 }}>
          {fmtInt(units)} ед.
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function StocksCost() {
  const { error: showError, success: showSuccess, info: showInfo } = useNotify();

  const [filters, setFilters] = useState({});
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [rows, setRows] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null); // момент последней загрузки остатков
  const [polling, setPolling] = useState(false); // ожидаем свежие данные после «Обновить»
  const [cut, setCut] = useState('gruppa'); // donut: gruppa | mp | warehouse
  const [rowLevel, setRowLevel] = useState('products'); // matrix: products | groups | subgroups
  const versionRef = useRef(0);
  const pollRef = useRef(null); // { timer, attempts, baseline }

  // Загрузка данных. Возвращает актуальный timestamp свежести (для polling'а).
  const load = useCallback(async () => {
    const version = ++versionRef.current;
    setLoading(true);
    try {
      const params = buildFilterParams(filters);
      if (date) params.date = date;
      const resp = await axios.get('/products/stocks', { params });
      if (version !== versionRef.current) return null;
      setRows(Array.isArray(resp.data?.rows) ? resp.data.rows : []);
      const upd = resp.data?.lastUpdated || null;
      setLastUpdated(upd);
      setLoaded(true);
      return upd;
    } catch (e) {
      if (version === versionRef.current) {
        showError(e?.response?.data?.message || e?.message || 'Ошибка загрузки');
      }
      return null;
    } finally {
      if (version === versionRef.current) setLoading(false);
    }
  }, [filters, date, showError]);

  // Останавливаем polling при размонтировании.
  useEffect(() => () => {
    if (pollRef.current?.timer) clearTimeout(pollRef.current.timer);
  }, []);

  // Ручной запуск обновления остатков из маркетплейсов (ставит cron-задачи в очередь).
  // Учитывает выбранный МП: если выбран конкретный — обновляем только его, иначе все.
  // Лёгкий ограниченный polling: после запуска обновления периодически перечитываем
  // данные, пока timestamp свежести не изменится (или пока не исчерпаны попытки).
  const POLL_INTERVAL_MS = 30000;
  const POLL_MAX_ATTEMPTS = 6; // ~3 минуты
  const schedulePoll = useCallback((baseline) => {
    if (pollRef.current?.timer) clearTimeout(pollRef.current.timer);
    pollRef.current = { timer: null, attempts: 0, baseline };
    setPolling(true);

    const tick = async () => {
      pollRef.current.attempts += 1;
      const upd = await load();
      const changed = upd && upd !== pollRef.current.baseline;
      if (changed) {
        setPolling(false);
        pollRef.current.timer = null;
        showSuccess('Остатки обновлены.');
        return;
      }
      if (pollRef.current.attempts >= POLL_MAX_ATTEMPTS) {
        setPolling(false);
        pollRef.current.timer = null;
        showInfo('Данные ещё загружаются в фоне — нажмите «Загрузить» позже, чтобы проверить.');
        return;
      }
      pollRef.current.timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    pollRef.current.timer = setTimeout(tick, POLL_INTERVAL_MS);
  }, [load, showSuccess, showInfo]);

  const refreshStocks = useCallback(async () => {
    setRefreshing(true);
    try {
      const body = {};
      const mp = Number(filters?.mp);
      if (mp === 1 || mp === 2 || mp === 3) body.mp = mp;
      const resp = await axios.post('/products/stocks/refresh', body);
      const labels = (resp.data?.triggered || []).map((t) => t.label);
      const uniq = [...new Set(labels)];
      showSuccess(
        uniq.length
          ? `Обновление запущено: ${uniq.join(', ')}. Данные появятся через несколько минут.`
          : 'Обновление остатков запущено в фоне.',
      );
      showInfo('Загрузка из маркетплейсов идёт в фоне — данные подтянутся автоматически.');
      // Запускаем ограниченный polling: baseline = текущий timestamp свежести.
      schedulePoll(lastUpdated);
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || 'Не удалось запустить обновление');
    } finally {
      setRefreshing(false);
    }
  }, [filters, lastUpdated, schedulePoll, showSuccess, showInfo, showError]);

  // ---------- KPI ----------
  const kpi = useMemo(() => {
    let totalMoney = 0;
    let totalUnits = 0;
    let stockMoney = 0;
    let stockUnits = 0;
    let transitMoney = 0;
    let transitUnits = 0;
    const skuSet = new Set();
    for (const r of rows) {
      const qty = Number(r.qty || 0);
      const cost = Number(r.cost || 0);
      const money = qty * cost;
      skuSet.add(`${r.organization}|${r.mp}|${r.product_id}|${r.offer_id}`);
      totalMoney += money;
      totalUnits += qty;
      if (r.bucket === 'stock') {
        stockMoney += money;
        stockUnits += qty;
      } else if (TRANSIT_BUCKETS.has(r.bucket)) {
        transitMoney += money;
        transitUnits += qty;
      }
    }
    return { totalMoney, totalUnits, stockMoney, stockUnits, transitMoney, transitUnits, skuCount: skuSet.size };
  }, [rows]);

  // ---------- Donut (все bucket'ы: stock + транзит) ----------
  const donut = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const money = Number(r.qty || 0) * Number(r.cost || 0);
      let key;
      if (cut === 'mp') key = MP_MAP[r.mp] || `МП ${r.mp}`;
      else if (cut === 'warehouse') {
        key = TRANSIT_BUCKETS.has(r.bucket)
          ? (TRANSIT_LABEL_BY_BUCKET[r.bucket] || r.bucket)
          : (r.warehouse_name || '—');
      } else key = r.gruppa || '—';
      map.set(key, (map.get(key) || 0) + money);
    }
    let items = Array.from(map.entries())
      .map(([label, value]) => ({ label, value }))
      .filter((it) => it.value > 0)
      .sort((a, b) => b.value - a.value);

    let total = items.reduce((s, it) => s + it.value, 0);
    if (items.length > TOP_N) {
      const head = items.slice(0, TOP_N);
      const restValue = items.slice(TOP_N).reduce((s, it) => s + it.value, 0);
      head.push({ label: 'Прочее', value: restValue });
      items = head;
    }
    items = items.map((it, i) => ({
      ...it,
      key: `${it.label}_${i}`,
      share_pct: total > 0 ? (it.value / total) * 100 : 0,
    }));
    return { items, total };
  }, [rows, cut]);

  // ---------- Матрица (pivot) ----------
  const matrix = useMemo(() => {
    // 1. Собираем столбцы: физсклады (по убыванию ₽) + транзит (если есть qty)
    const stockColMoney = new Map(); // warehouse_name -> сумма ₽
    const transitQty = new Map(); // bucket -> qty
    for (const r of rows) {
      const qty = Number(r.qty || 0);
      const cost = Number(r.cost || 0);
      if (r.bucket === 'stock') {
        const name = r.warehouse_name || '—';
        stockColMoney.set(name, (stockColMoney.get(name) || 0) + qty * cost);
      } else if (TRANSIT_BUCKETS.has(r.bucket)) {
        transitQty.set(r.bucket, (transitQty.get(r.bucket) || 0) + qty);
      }
    }
    const stockCols = Array.from(stockColMoney.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => ({ key: `wh:${name}`, label: name, kind: 'stock' }));
    const transitCols = TRANSIT_COLUMNS
      .filter((c) => (transitQty.get(c.key) || 0) > 0)
      .map((c) => ({ key: c.key, label: c.label, kind: 'transit' }));
    const columns = [...stockCols, ...transitCols];

    // 2. Pivot: rowKey -> { meta, cells: {colKey: {qty, sum}}, total: {qty, sum} }
    const pivot = new Map();
    for (const r of rows) {
      const qty = Number(r.qty || 0);
      const cost = Number(r.cost || 0);
      const money = qty * cost;

      let rowKey;
      let meta;
      if (rowLevel === 'groups') {
        rowKey = r.gruppa || '—';
        meta = { name: r.gruppa || '—' };
      } else if (rowLevel === 'subgroups') {
        rowKey = `${r.gruppa || ''}|${r.podgruppa || '—'}`;
        meta = { name: r.podgruppa || '—', gruppa: r.gruppa || '' };
      } else {
        rowKey = `${r.organization}|${r.mp}|${r.product_id}|${r.offer_id}`;
        meta = {
          offer_id: r.offer_id,
          mp: r.mp,
          product_id: r.product_id,
          image_url: r.image_url,
          organization: r.organization,
          brand: r.brand,
        };
      }

      const colKey = r.bucket === 'stock' ? `wh:${r.warehouse_name || '—'}` : r.bucket;

      let entry = pivot.get(rowKey);
      if (!entry) {
        entry = { key: rowKey, meta, cells: {}, total: { qty: 0, sum: 0 } };
        pivot.set(rowKey, entry);
      }
      const cell = entry.cells[colKey] || { qty: 0, sum: 0 };
      cell.qty += qty;
      cell.sum += money;
      entry.cells[colKey] = cell;
      entry.total.qty += qty;
      entry.total.sum += money;
    }

    const pivotRows = Array.from(pivot.values()).sort((a, b) => b.total.sum - a.total.sum);

    // 3. Итоги по столбцам + общий итог
    const colTotals = {};
    let grand = { qty: 0, sum: 0 };
    for (const col of columns) colTotals[col.key] = { qty: 0, sum: 0 };
    for (const pr of pivotRows) {
      for (const col of columns) {
        const c = pr.cells[col.key];
        if (c) {
          colTotals[col.key].qty += c.qty;
          colTotals[col.key].sum += c.sum;
        }
      }
      grand.qty += pr.total.qty;
      grand.sum += pr.total.sum;
    }

    return { columns, pivotRows, colTotals, grand };
  }, [rows, rowLevel]);

  // ---------- styles ----------
  const cellSx = { py: 0.5, px: 1, fontSize: '0.8rem', whiteSpace: 'nowrap' };
  const headerSx = { ...cellSx, fontWeight: 'bold', bgcolor: 'grey.100' };
  const firstColSx = {
    ...cellSx,
    position: 'sticky',
    left: 0,
    zIndex: 2,
    bgcolor: 'background.paper',
    borderRight: '1px solid',
    borderColor: 'divider',
  };
  const firstColHeaderSx = { ...headerSx, position: 'sticky', left: 0, zIndex: 4, minWidth: 220 };
  const totalColSx = {
    ...cellSx,
    position: 'sticky',
    right: 0,
    zIndex: 2,
    bgcolor: 'grey.50',
    fontWeight: 600,
    borderLeft: '1px solid',
    borderColor: 'divider',
  };
  const totalColHeaderSx = { ...headerSx, position: 'sticky', right: 0, zIndex: 4, bgcolor: 'grey.200' };

  const renderCell = (cell) => {
    if (!cell || (!cell.qty && !cell.sum)) {
      return <Typography component="span" sx={{ color: 'text.disabled' }}>—</Typography>;
    }
    return (
      <Box sx={{ lineHeight: 1.15 }}>
        <Typography component="div" sx={{ fontSize: '0.85rem', fontWeight: 600 }}>
          {fmtInt(cell.qty)}
        </Typography>
        <Typography component="div" sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>
          {fmtMoney(cell.sum)} ₽
        </Typography>
      </Box>
    );
  };

  const renderRowHead = (meta) => {
    if (rowLevel === 'products') {
      const imgSrc = meta.mp === 1
        ? (toThumbnail(meta.image_url) || getWbImageUrl(meta.product_id))
        : toThumbnail(meta.image_url);
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {imgSrc ? (
            <Avatar variant="rounded" src={imgSrc} sx={{ width: 36, height: 36 }} imgProps={{ loading: 'lazy' }}>—</Avatar>
          ) : (
            <Avatar variant="rounded" sx={{ width: 36, height: 36, bgcolor: 'grey.200', color: 'grey.500', fontSize: 12 }}>—</Avatar>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Tooltip title={meta.brand || ''} placement="top" arrow>
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
                {meta.offer_id || '—'}
              </Typography>
            </Tooltip>
            <Chip
              label={MP_MAP[meta.mp] || meta.mp}
              size="small"
              sx={{
                bgcolor: MP_COLORS[meta.mp] || 'grey.400',
                color: MP_TEXT_COLORS[meta.mp] || '#fff',
                fontWeight: 600,
                fontSize: '0.68rem',
                height: 18,
              }}
            />
          </Box>
        </Box>
      );
    }
    return (
      <Box>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 500, whiteSpace: 'nowrap' }}>
          {meta.name || '—'}
        </Typography>
        {rowLevel === 'subgroups' && meta.gruppa && (
          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
            {meta.gruppa}
          </Typography>
        )}
      </Box>
    );
  };

  const colCount = matrix.columns.length + 2; // первая колонка + столбцы + Итого

  return (
    <Box>
      <PageHeader title="Остатки и себестоимость" />

      {/* Фильтры */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ mb: 2 }}>
          <ProductFilters value={filters} onChange={setFilters} showArticle={false} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Дата снимка"
            type="date"
            size="small"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 200 }}
          />
          <Button
            variant="contained"
            onClick={load}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          >
            Загрузить
          </Button>
          <Tooltip title="Запустить загрузку остатков из маркетплейсов прямо сейчас (данные обновятся в фоне через несколько минут)" arrow>
            <span>
              <Button
                variant="outlined"
                onClick={refreshStocks}
                disabled={refreshing || polling}
                startIcon={(refreshing || polling) ? <CircularProgress size={18} /> : <CloudSyncIcon />}
              >
                {polling ? 'Ожидаем данные…' : 'Обновить остатки'}
              </Button>
            </span>
          </Tooltip>
          <Box sx={{ display: 'flex', flexDirection: 'column', ml: { xs: 0, sm: 'auto' } }}>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
              Обновлено
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              {loaded ? fmtDateTime(lastUpdated) : '—'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* KPI */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <KpiCard
            title="Всего"
            money={kpi.totalMoney}
            units={kpi.totalUnits}
            icon={<AllInboxIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KpiCard
            title="В пути"
            money={kpi.transitMoney}
            units={kpi.transitUnits}
            icon={<LocalShippingIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KpiCard
            title="На складах"
            money={kpi.stockMoney}
            units={kpi.stockUnits}
            icon={<WarehouseIcon />}
            color="success"
          />
        </Grid>
      </Grid>

      {/* Donut */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Стоимость остатков в разрезе</Typography>
            <Typography variant="body2" color="text.secondary">
              Итого: {fmtMoney(donut.total)} ₽
            </Typography>
          </Box>
          <ToggleButtonGroup
            value={cut}
            exclusive
            size="small"
            onChange={(_, v) => v && setCut(v)}
          >
            <ToggleButton value="gruppa">Группа</ToggleButton>
            <ToggleButton value="mp">Маркетплейс</ToggleButton>
            <ToggleButton value="warehouse">Склад</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {donut.items.length ? (
          <Grid container spacing={1} sx={{ height: 300 }}>
            <Grid item xs={12} md={6} sx={{ minHeight: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={donut.items} dataKey="value" nameKey="label" innerRadius="55%" outerRadius="85%" paddingAngle={1}>
                    {donut.items.map((entry, index) => (
                      <Cell key={entry.key} fill={chartColors[index % chartColors.length]} />
                    ))}
                  </Pie>
                  <ChartTooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const item = payload[0]?.payload;
                      if (!item) return null;
                      return (
                        <Paper sx={{ p: 1, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>{item.label}</Typography>
                          <Typography variant="caption" sx={{ display: 'block' }}>{fmtMoney(item.value)} ₽</Typography>
                          <Typography variant="caption" color="text.secondary">{fmtPct(item.share_pct)}</Typography>
                        </Paper>
                      );
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Grid>
            <Grid item xs={12} md={6} sx={{ maxHeight: 280, overflow: 'auto' }}>
              {donut.items.map((item, index) => (
                <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: chartColors[index % chartColors.length], flex: '0 0 auto' }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="caption" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmtMoney(item.value)} ₽ · {fmtPct(item.share_pct)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
            {loaded ? 'Нет данных по остаткам' : 'Нажмите «Загрузить» для получения данных'}
          </Box>
        )}
      </Paper>

      {/* Матрица */}
      <Paper sx={{ overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>Сводная матрица: остатки по складам</Typography>
          <ToggleButtonGroup
            value={rowLevel}
            exclusive
            size="small"
            onChange={(_, v) => v && setRowLevel(v)}
          >
            <ToggleButton value="products">Товары</ToggleButton>
            <ToggleButton value="groups">Группы</ToggleButton>
            <ToggleButton value="subgroups">Подгруппы</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {(loading || polling) && <LinearProgress />}

        <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={firstColHeaderSx}>
                  {rowLevel === 'products' ? 'Товар' : rowLevel === 'groups' ? 'Группа' : 'Подгруппа'}
                </TableCell>
                {matrix.columns.map((col) => (
                  <TableCell key={col.key} sx={headerSx} align="right">
                    <Tooltip title={col.label} placement="top" arrow>
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-block',
                          maxWidth: 120,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          verticalAlign: 'bottom',
                        }}
                      >
                        {col.label}
                      </Box>
                    </Tooltip>
                  </TableCell>
                ))}
                <TableCell sx={totalColHeaderSx} align="right">
                  <TableSortLabel active direction="desc" hideSortIcon={false}>
                    Итого
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} align="center" sx={{ py: 6 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : matrix.pivotRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={colCount} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">
                      {loaded ? 'Нет данных' : 'Нажмите «Загрузить» для получения данных'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                matrix.pivotRows.map((pr) => (
                  <TableRow key={pr.key} hover>
                    <TableCell sx={firstColSx}>{renderRowHead(pr.meta)}</TableCell>
                    {matrix.columns.map((col) => (
                      <TableCell key={col.key} sx={cellSx} align="right">
                        {renderCell(pr.cells[col.key])}
                      </TableCell>
                    ))}
                    <TableCell sx={totalColSx} align="right">
                      {renderCell(pr.total)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
            {matrix.pivotRows.length > 0 && (
              <TableFooter>
                <TableRow>
                  <TableCell sx={{ ...firstColSx, bgcolor: 'grey.100', zIndex: 3, fontWeight: 'bold' }}>
                    Итого
                  </TableCell>
                  {matrix.columns.map((col) => (
                    <TableCell key={col.key} sx={{ ...cellSx, bgcolor: 'grey.100', fontWeight: 600 }} align="right">
                      {renderCell(matrix.colTotals[col.key])}
                    </TableCell>
                  ))}
                  <TableCell sx={{ ...totalColSx, bgcolor: 'grey.200', zIndex: 3, fontWeight: 'bold' }} align="right">
                    {renderCell(matrix.grand)}
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
